import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../services/api';
import {
  CITY_TO_DEPARTMENT,
  DEFAULT_REVIEW_REGION,
  DEPARTMENT_TO_CITIES,
  DEPARTMENT_WITH_CITIES_SET,
  PARIS_MERGED_DEPARTMENTS,
  PARIS_REVIEW_DEPARTMENTS,
  getDepartmentDisplayCities,
  getReviewCityDisplayName,
  normalizeName,
  resolveRegionName,
  toRegionKey,
} from '../utils/testimonialsMapData';
import './Temoignages.css';

const MAP_ACTIVE_RED = '#DC143C';
const MAP_ACTIVE_BLUE = '#0A0E60';
const MAP_ACTIVE_RED_HOVER = '#FF1744';
const MAP_ACTIVE_BLUE_HOVER = '#1A1E70';
const BOTTOM_FADE_MAX_DISTANCE = 140;
const BOTTOM_FADE_LATE_START_PROGRESS = 0.6;
const BOTTOM_FADE_ACCELERATION = 2.35;

const departmentsWithCities = DEPARTMENT_WITH_CITIES_SET;

const COLORS_FOR_ACTIVE_DEPARTMENTS = [MAP_ACTIVE_RED, MAP_ACTIVE_BLUE];

const safeGetBBox = (path) => {
  try {
    return path.getBBox();
  } catch (_error) {
    return null;
  }
};

const arePathsAdjacent = (boxA, boxB, padding = 0.8) => {
  if (!boxA || !boxB) return false;

  return !(
    boxA.x + boxA.width + padding < boxB.x ||
    boxB.x + boxB.width + padding < boxA.x ||
    boxA.y + boxA.height + padding < boxB.y ||
    boxB.y + boxB.height + padding < boxA.y
  );
};

const assignDepartmentColors = (entries) => {
  const adjacency = new Map();
  const boxes = new Map();

  entries.forEach(({ regionName, path }) => {
    adjacency.set(regionName, new Set());
    boxes.set(regionName, safeGetBBox(path));
  });

  for (let i = 0; i < entries.length; i += 1) {
    for (let j = i + 1; j < entries.length; j += 1) {
      const regionA = entries[i].regionName;
      const regionB = entries[j].regionName;

      if (arePathsAdjacent(boxes.get(regionA), boxes.get(regionB))) {
        adjacency.get(regionA).add(regionB);
        adjacency.get(regionB).add(regionA);
      }
    }
  }

  const orderedRegions = [...adjacency.keys()].sort((a, b) => {
    const degreeDiff = adjacency.get(b).size - adjacency.get(a).size;
    if (degreeDiff !== 0) return degreeDiff;
    return a.localeCompare(b);
  });

  const assigned = new Map();

  orderedRegions.forEach((regionName) => {
    const neighbors = adjacency.get(regionName);
    let bestColor = MAP_ACTIVE_RED;
    let lowestConflictCount = Number.POSITIVE_INFINITY;

    COLORS_FOR_ACTIVE_DEPARTMENTS.forEach((candidateColor) => {
      let conflictCount = 0;
      neighbors.forEach((neighbor) => {
        if (assigned.get(neighbor) === candidateColor) {
          conflictCount += 1;
        }
      });

      if (
        conflictCount < lowestConflictCount ||
        (conflictCount === lowestConflictCount && candidateColor === MAP_ACTIVE_RED)
      ) {
        bestColor = candidateColor;
        lowestConflictCount = conflictCount;
      }
    });

    assigned.set(regionName, bestColor);
  });

  // Local improvement pass to reduce same-color adjacent pairs when possible.
  orderedRegions.forEach((regionName) => {
    const current = assigned.get(regionName);
    const alternate = current === MAP_ACTIVE_RED ? MAP_ACTIVE_BLUE : MAP_ACTIVE_RED;
    const neighbors = adjacency.get(regionName);

    const currentConflicts = [...neighbors].filter((neighbor) => assigned.get(neighbor) === current).length;
    const alternateConflicts = [...neighbors].filter((neighbor) => assigned.get(neighbor) === alternate).length;

    if (alternateConflicts < currentConflicts) {
      assigned.set(regionName, alternate);
    }
  });

  return assigned;
};

const Temoignages = () => {
  const [hoveredCity, setHoveredCity] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [svgContent, setSvgContent] = useState(null);
  const [allReviews, setAllReviews] = useState([]);
  const [displayCount, setDisplayCount] = useState(5);
  const [expandedReviews, setExpandedReviews] = useState(new Set());
  const [selectedRegion, setSelectedRegion] = useState(DEFAULT_REVIEW_REGION);
  const [truncatedReviews, setTruncatedReviews] = useState(new Set());
  const [showScrollTopFade, setShowScrollTopFade] = useState(false);
  const [bottomFadeOpacity, setBottomFadeOpacity] = useState(0);
  const [visibleReviews, setVisibleReviews] = useState(new Set());
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [regionInfoByKey, setRegionInfoByKey] = useState({});
  const [showCreateReviewModal, setShowCreateReviewModal] = useState(false);
  const [reviewFormFields, setReviewFormFields] = useState([]);
  const [reviewDraft, setReviewDraft] = useState({
    reviewer_name: '',
    rating: 0,
    review_text: '',
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewSubmitError, setReviewSubmitError] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const svgContainerRef = useRef(null);
  const reviewsListRef = useRef(null);
  const reviewRefs = useRef({});

  const departmentToCities = DEPARTMENT_TO_CITIES;
  const resolvedSelectedRegion = resolveRegionName(selectedRegion || DEFAULT_REVIEW_REGION);
  const selectedCities = getDepartmentDisplayCities(resolvedSelectedRegion);
  const selectedRegionInfo = regionInfoByKey[toRegionKey(resolvedSelectedRegion)]
    || `Informations generales: ${selectedCities.join(', ')} est une zone active pour les retours locataires. Consultez les avis pour mieux comprendre l'experience des residents.`;

  const updateReviewsScrollIndicators = useCallback(() => {
    const listEl = reviewsListRef.current;
    if (!listEl) {
      setShowScrollTopFade(false);
      setBottomFadeOpacity(0);
      return;
    }

    const { scrollTop, clientHeight, scrollHeight } = listEl;
    const hasOverflow = scrollHeight > clientHeight + 2;
    const remainingDistance = Math.max(0, scrollHeight - (scrollTop + clientHeight));
    let nextBottomFadeOpacity = 0;

    if (hasOverflow && remainingDistance > 1) {
      const normalizedRemaining = Math.min(1, remainingDistance / BOTTOM_FADE_MAX_DISTANCE);
      const progressToBottom = 1 - normalizedRemaining;

      if (progressToBottom <= BOTTOM_FADE_LATE_START_PROGRESS) {
        nextBottomFadeOpacity = 1;
      } else {
        const fadeProgress =
          (progressToBottom - BOTTOM_FADE_LATE_START_PROGRESS) /
          (1 - BOTTOM_FADE_LATE_START_PROGRESS);
        nextBottomFadeOpacity = Math.pow(Math.max(0, 1 - fadeProgress), BOTTOM_FADE_ACCELERATION);
      }
    }

    setShowScrollTopFade(hasOverflow && scrollTop > 2);
    setBottomFadeOpacity(nextBottomFadeOpacity);
  }, []);

  const fetchReviews = useCallback(() => {
    const region = resolveRegionName(selectedRegion || DEFAULT_REVIEW_REGION);
    const reviewDepartments = normalizeName(region) === normalizeName(DEFAULT_REVIEW_REGION)
      ? PARIS_REVIEW_DEPARTMENTS
      : [region];

    setIsLoadingReviews(true);

    const requests = reviewDepartments.map((department) => (
      api.reviews.listByDepartment(department)
        .then((response) => ({
          ok: true,
          department,
          data: response?.data?.data,
        }))
        .catch((error) => ({ ok: false, department, error }))
    ));

    Promise.all(requests)
      .then((results) => {
        const merged = [];

        results.forEach((result) => {
          if (!result.ok) {
            console.error(`Error loading reviews for ${result.department}:`, result.error);
            return;
          }

          if (Array.isArray(result.data)) {
            merged.push(...result.data);
          }
        });

        const deduped = new Map();
        merged.forEach((review) => {
          if (!review || review.id == null) return;
          if (!deduped.has(review.id)) {
            deduped.set(review.id, review);
          }
        });

        const sorted = [...deduped.values()].sort((a, b) => {
          const aTime = Date.parse(a.created_at || 0) || 0;
          const bTime = Date.parse(b.created_at || 0) || 0;
          return bTime - aTime;
        });

        setAllReviews(sorted);
        setDisplayCount(5);
      })
      .catch((error) => {
        console.error('Error loading reviews:', error);
        setAllReviews([]);
        setDisplayCount(5);
      })
      .finally(() => {
        setIsLoadingReviews(false);
      });
  }, [selectedRegion]);

  useEffect(() => {
    let cancelled = false;
    api.reviews.listRegionInfoPublic()
      .then((response) => {
        if (cancelled) return;
        const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
        const next = {};
        const parisKey = toRegionKey(DEFAULT_REVIEW_REGION);
        const normalizedParis = normalizeName(DEFAULT_REVIEW_REGION);

        rows.forEach((row) => {
          const rawKey = String(row?.region_key || '').trim();
          const normalizedKey = normalizeName(rawKey);
          const key = PARIS_MERGED_DEPARTMENTS.has(normalizedKey)
            ? parisKey
            : toRegionKey(rawKey);
          if (!key) return;
          const value = String(row?.info_text || '').trim();

          if (key === parisKey) {
            if (normalizedKey === normalizedParis || !next[parisKey]) {
              next[parisKey] = value;
            }
            return;
          }

          next[key] = value;
        });
        setRegionInfoByKey(next);
      })
      .catch((error) => {
        console.error('Error loading region info:', error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.reviews.getFormConfigPublic()
      .then((response) => {
        if (cancelled) return;
        const fields = Array.isArray(response?.data?.data) ? response.data.data : [];
        if (fields.length > 0) {
          setReviewFormFields(fields);
          const draft = {};
          fields.forEach(f => {
            if (f.type === 'rating') { draft.rating = 0; }
            else if (f.type === 'checkbox') { draft[f.id] = false; }
            else { draft[f.id] = ''; }
          });
          setReviewDraft(prev => ({ ...draft, ...prev }));
        }
      })
      .catch((error) => {
        console.error('Error loading review form config:', error);
      });
    return () => { cancelled = true; };
  }, []);

  const openCreateReviewModal = () => {
    setReviewSubmitError('');
    setHoverRating(0);
    setShowCreateReviewModal(true);
  };

  const closeCreateReviewModal = () => {
    if (reviewSubmitting) return;
    setShowCreateReviewModal(false);
    setReviewSubmitError('');
  };

  const submitReview = async () => {
    const rating = Number(reviewDraft.rating);

    if (!rating || rating < 1 || rating > 5) {
      setReviewSubmitError('Veuillez selectionner une note entre 1 et 5.');
      return;
    }

    const requiredFields = reviewFormFields.filter(f => f.required && f.type !== 'rating');
    for (const field of requiredFields) {
      const val = reviewDraft[field.id];
      if (field.type === 'checkbox' && Array.isArray(field.options) && field.options.length > 0) {
        if (!Array.isArray(val) || val.length === 0) {
          setReviewSubmitError(`Veuillez renseigner le champ: ${field.label}`);
          return;
        }
      } else if (val === undefined || val === null || String(val).trim() === '') {
        setReviewSubmitError(`Veuillez renseigner le champ: ${field.label}`);
        return;
      }
    }

    const reviewerName = String(reviewDraft.reviewer_name || '').trim() || 'Anonyme';
    const reviewText = String(reviewDraft.review_text || '').trim() || '';

    try {
      setReviewSubmitting(true);
      setReviewSubmitError('');
      const customData = {};
      reviewFormFields.forEach(f => {
        if (f.type !== 'rating' && f.id !== 'reviewer_name' && f.id !== 'review_text') {
          const val = reviewDraft[f.id];
          if (val !== undefined && val !== null && String(val).trim() !== '') {
            customData[f.id] = val;
          }
        }
      });
      const payload = {
        reviewer_name: reviewerName,
        department: selectedRegion,
        city: selectedCities[0] || selectedRegion,
        rating,
        review_text: reviewText,
        custom_fields_data: Object.keys(customData).length > 0 ? customData : null,
      };
      await api.reviews.create(payload);
      setShowCreateReviewModal(false);
      const resetDraft = { reviewer_name: '', rating: 0, review_text: '' };
      reviewFormFields.forEach(f => {
        if (f.type !== 'rating' && f.id !== 'reviewer_name' && f.id !== 'review_text') {
          if (f.type === 'checkbox' && Array.isArray(f.options) && f.options.length > 0) {
            resetDraft[f.id] = [];
          } else if (f.type === 'checkbox') {
            resetDraft[f.id] = false;
          } else {
            resetDraft[f.id] = '';
          }
        }
      });
      setReviewDraft(resetDraft);
      fetchReviews();
    } catch (error) {
      console.error('Failed to submit review:', error);
      setReviewSubmitError(error?.response?.data?.error || 'Impossible de publier votre avis pour le moment.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleRegionClick = useCallback((regionName) => {
    // Just set the region, don't toggle
    const resolvedRegion = resolveRegionName(regionName || DEFAULT_REVIEW_REGION);
    setSelectedRegion(resolvedRegion);
    setDisplayCount(5);
    setExpandedReviews(new Set());
    setTruncatedReviews(new Set());
    setVisibleReviews(new Set());
  }, []);

  const handleBackgroundClick = useCallback(() => {
    // Reset to Paris when clicking outside active departments.
    setSelectedRegion(DEFAULT_REVIEW_REGION);
    setDisplayCount(5);
    setExpandedReviews(new Set());
    setTruncatedReviews(new Set());
    setVisibleReviews(new Set());
  }, []);

  useEffect(() => {
    // Load the SVG file
    fetch('/fr.svg')
      .then(response => response.text())
      .then(svgText => {
        setSvgContent(svgText);
      })
      .catch(error => {
        console.error('Error loading SVG:', error);
      });
  }, []);

  // Separate useEffect for fetching reviews when region changes
  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  useEffect(() => {
    if (!svgContent || !svgContainerRef.current) return;

    // Parse the SVG content
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgElement = svgDoc.querySelector('svg');
    
    if (!svgElement) return;

    // Normalize source attributes so the SVG always scales to fit without clipping.
    const normalizedViewBox =
      svgElement.getAttribute('viewBox') ||
      svgElement.getAttribute('viewbox');

    if (normalizedViewBox) {
      svgElement.setAttribute('viewBox', normalizedViewBox);
    } else {
      const width = Number(svgElement.getAttribute('width')) || 1000;
      const height = Number(svgElement.getAttribute('height')) || 960;
      svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
    }

    svgElement.removeAttribute('viewbox');
    svgElement.removeAttribute('width');
    svgElement.removeAttribute('height');
    svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svgElement.style.width = '100%';
    svgElement.style.height = 'auto';
    svgElement.style.display = 'block';

    // Clear the container and add the modified SVG first so getBBox works consistently.
    svgContainerRef.current.innerHTML = '';
    svgContainerRef.current.appendChild(svgElement);

    // Get all path elements (each represents a region)
    const paths = svgElement.querySelectorAll('path[name]');
    const activeEntries = [];

    paths.forEach(path => {
      const regionName = path.getAttribute('name');
      const normalizedRegionName = normalizeName(regionName);
      
      // Check if this department has any of our cities
      const isInList = departmentsWithCities.has(normalizedRegionName);
      
      if (isInList) {
        activeEntries.push({ regionName, path });
        path.setAttribute('class', 'region-active');
        path.style.cursor = 'pointer';
      } else {
        // Black for cities not in the list
        path.setAttribute('fill', '#000000');
        path.setAttribute('class', 'region-inactive');
        path.style.cursor = 'default';
        
        // Add click handler to reset to Paris
        path.addEventListener('click', (e) => {
          e.stopPropagation();
          handleBackgroundClick();
        });
      }
      
      // Add stroke for better visibility
      path.setAttribute('stroke', '#ffffff');
      path.setAttribute('stroke-width', '0.5');
    });

    const assignedColors = assignDepartmentColors(activeEntries);

    activeEntries.forEach(({ regionName, path }) => {
      const isParisMergedRegion = PARIS_MERGED_DEPARTMENTS.has(normalizeName(regionName));
      const baseColor = isParisMergedRegion ? MAP_ACTIVE_RED : (assignedColors.get(regionName) || MAP_ACTIVE_RED);
      const hoverColor = isParisMergedRegion
        ? MAP_ACTIVE_RED
        : (baseColor === MAP_ACTIVE_BLUE ? MAP_ACTIVE_BLUE_HOVER : MAP_ACTIVE_RED_HOVER);

      path.setAttribute('fill', baseColor);
      path.setAttribute('data-base-fill', baseColor);
      path.setAttribute('data-hover-fill', hoverColor);
      path.setAttribute('stroke', isParisMergedRegion ? baseColor : '#ffffff');
      path.setAttribute('stroke-width', isParisMergedRegion ? '1' : '0.5');

      path.addEventListener('mouseenter', () => {
        setHoveredCity(resolveRegionName(regionName));
        path.setAttribute('fill', path.getAttribute('data-hover-fill') || baseColor);
      });

      path.addEventListener('mouseleave', () => {
        setHoveredCity(null);
        path.setAttribute('fill', path.getAttribute('data-base-fill') || baseColor);
      });

      path.addEventListener('mousemove', (e) => {
        setMousePosition({ x: e.clientX, y: e.clientY });
      });

      path.addEventListener('click', () => {
        handleRegionClick(regionName);
      });
    });

    // Add click handler to SVG background
    svgElement.addEventListener('click', (e) => {
      // Only trigger if clicking directly on SVG background (not on a path)
      if (e.target === svgElement) {
        handleBackgroundClick();
      }
    });

  }, [svgContent, handleRegionClick, handleBackgroundClick]);

  const toggleReview = (reviewId) => {
    setExpandedReviews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  const renderRatingBadge = (rating) => {
    const normalizedRating = Math.max(0, Math.min(5, Number(rating) || 0));
    const filledStars = Math.round(normalizedRating);
    const emptyStars = Math.max(0, 5 - filledStars);

    return (
      <span className="review-stars-inline" aria-label={`${normalizedRating.toFixed(1)} sur 5 etoiles`}>
          <span className="review-stars-filled">{'★'.repeat(filledStars)}</span>
          {emptyStars > 0 && <span className="review-stars-empty">{'★'.repeat(emptyStars)}</span>}
      </span>
    );
  };

  const handleShowMore = () => {
    setDisplayCount(prev => prev + 5);
  };

  // Calculate which reviews to display
  const reviews = allReviews.slice(0, displayCount);
  const hasMore = allReviews.length > displayCount;
  const isEmptyState = !isLoadingReviews && reviews.length === 0;

  useEffect(() => {
    if (reviews.length === 0 || isLoadingReviews) {
      setVisibleReviews(new Set());
      return;
    }

    const listEl = reviewsListRef.current;
    if (!listEl) return;

    if (typeof window !== 'undefined' && !('IntersectionObserver' in window)) {
      setVisibleReviews(new Set(reviews.map((review) => String(review.id))));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleReviews((prev) => {
          const next = new Set(prev);
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const reviewId = entry.target.getAttribute('data-review-id');
              if (reviewId) {
                next.add(reviewId);
              }
            }
          });
          return next;
        });
      },
      {
        root: listEl,
        threshold: 0.2,
        rootMargin: '0px 0px -12% 0px',
      }
    );

    const cards = listEl.querySelectorAll('.review-card[data-review-id]');
    cards.forEach((card) => observer.observe(card));

    return () => {
      observer.disconnect();
    };
  }, [reviews, isLoadingReviews]);

  useEffect(() => {
    if (typeof window === 'undefined' || isLoadingReviews || reviews.length === 0) {
      return undefined;
    }

    // Fallback: if IntersectionObserver does not emit quickly, reveal all cards.
    const timeoutId = window.setTimeout(() => {
      setVisibleReviews((prev) => {
        if (prev.size > 0) {
          return prev;
        }
        return new Set(reviews.map((review) => String(review.id)));
      });
    }, 420);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [reviews, isLoadingReviews]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const frame = window.requestAnimationFrame(() => {
      updateReviewsScrollIndicators();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [reviews, isLoadingReviews, updateReviewsScrollIndicators]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onResize = () => updateReviewsScrollIndicators();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [updateReviewsScrollIndicators]);

  // Check which reviews are actually truncated
  useEffect(() => {
    if (reviews.length === 0) {
      setTruncatedReviews(new Set());
      return;
    }

    const newTruncated = new Set();
    reviews.forEach(review => {
      const element = reviewRefs.current[review.id];
      if (element && element.scrollHeight > element.clientHeight) {
        newTruncated.add(review.id);
      }
    });
    setTruncatedReviews(newTruncated);
  }, [reviews, expandedReviews]);

  return (
    <div className="temoignages-map-page">
      {/* Reviews Container */}
      <div className={`reviews-container ${isEmptyState ? 'is-empty' : ''}`}>
        <div className="reviews-head-row">
          <h2 className="reviews-title">{selectedCities.join(', ')}</h2>
          <button type="button" className="reviews-cta-btn" onClick={openCreateReviewModal}>Laisser une note</button>
        </div>
        <section className="reviews-info-box">
          <div className="reviews-info-label">Informations generales</div>
          <p className="reviews-info-copy">{selectedRegionInfo}</p>
        </section>
        <div
          className="reviews-scroll-shell"
          style={{ '--reviews-bottom-fade-opacity': bottomFadeOpacity }}
        >
          <div className={`reviews-scroll-fade reviews-scroll-fade-top ${showScrollTopFade ? 'visible' : ''}`} />
          <div className="reviews-scroll-fade reviews-scroll-fade-bottom" style={{ opacity: bottomFadeOpacity }} />
          <div
            ref={reviewsListRef}
            className="reviews-list"
            onScroll={updateReviewsScrollIndicators}
          >
            {isLoadingReviews ? (
              <div className="review-loading-list" aria-hidden="true">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="review-skeleton">
                    <div className="review-skeleton-row review-skeleton-user" />
                    <div className="review-skeleton-row review-skeleton-city" />
                    <div className="review-skeleton-row review-skeleton-text" />
                    <div className="review-skeleton-row review-skeleton-text short" />
                  </div>
                ))}
              </div>
            ) : reviews.length === 0 ? (
              <div className="reviews-empty-state" role="status" aria-live="polite">
                <div className="reviews-empty-icon" aria-hidden="true">★</div>
                <span className="reviews-empty-eyebrow">Aucun temoignage pour le moment</span>
                <h3 className="reviews-empty-title">Aucun avis dans cette ville pour le moment</h3>
                <p className="reviews-empty-copy">
                  Nous n'avons pas encore de retours pour <span className="reviews-empty-region">{selectedCities.join(', ')}</span>.
                </p>
                <p className="reviews-empty-copy muted">
                  Essayez une autre zone surlignee sur la carte, ou revenez bientot.
                </p>
              </div>
            ) : reviews.map((review, index) => {
              const reviewId = String(review.id);
              const isExpanded = expandedReviews.has(review.id);
              const isTruncated = truncatedReviews.has(review.id);
              const fullName = String(review.reviewer_display_name || `${review.first_name || ''} ${review.last_name || ''}`).trim() || 'Anonyme';
              const isVisible = visibleReviews.size === 0 || visibleReviews.has(reviewId);

              const hasCustomFields = (() => {
                let d = review.custom_fields_data;
                if (d && typeof d === 'string') { try { d = JSON.parse(d); } catch { d = null; } }
                return d && typeof d === 'object' && Object.keys(d).length > 0;
              })();

              return (
                <div
                  key={review.id}
                  className={`review-card ${isVisible ? 'is-visible' : ''}`}
                  data-review-id={reviewId}
                  style={{ '--review-delay': `${Math.min(index * 60, 420)}ms` }}
                >
                  <div className="review-header">
                    <div className="review-user">{fullName}</div>
                    <div className="review-city">{getReviewCityDisplayName(review.city, review.department) || review.department}</div>
                    <div className="review-rating">{renderRatingBadge(review.rating)}</div>
                  </div>
                  <div className="review-body">
                    <div
                      ref={el => reviewRefs.current[review.id] = el}
                      className={`review-text ${isExpanded ? 'expanded' : 'collapsed'}`}
                    >
                      {review.review_text}
                    </div>
                    {(isTruncated || isExpanded || hasCustomFields) && (
                      <button
                        className="review-expand-btn"
                        onClick={() => toggleReview(review.id)}
                        aria-label={isExpanded ? "Replier l'avis" : "Deplier l'avis"}
                      >
                        <span className={`expand-arrow ${isExpanded ? 'rotated' : ''}`}>▼</span>
                      </button>
                    )}
                    {isExpanded && hasCustomFields && (() => {
                      let parsed = review.custom_fields_data;
                      if (typeof parsed === 'string') {
                        try { parsed = JSON.parse(parsed); } catch { parsed = null; }
                      }
                      if (!parsed || typeof parsed !== 'object') return null;
                      const fieldMap = {};
                      reviewFormFields.forEach(f => { fieldMap[f.id] = f; });
                      return (
                        <div className="review-custom-fields">
                          {Object.entries(parsed).map(([key, value]) => {
                            const field = fieldMap[key];
                            const label = field?.label || key;
                            let displayValue = value;
                            if (typeof value === 'boolean') displayValue = value ? 'Oui' : 'Non';
                            else if (Array.isArray(value)) displayValue = value.join(', ');
                            return (
                              <div key={key} className="review-custom-field">
                                <span className="review-custom-field-label">{label}:</span>
                                <span className="review-custom-field-value">{String(displayValue)}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
            {!isLoadingReviews && hasMore && (
              <button className="show-more-btn" onClick={handleShowMore}>
                Voir plus
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="france-map-container" onClick={handleBackgroundClick}>
        <div ref={svgContainerRef} className="svg-container" onClick={(e) => e.stopPropagation()} />
        {hoveredCity && (
          <div 
            className="city-tooltip"
            style={{
              left: `${mousePosition.x}px`,
              top: `${mousePosition.y}px`
            }}
          >
            {/* Show only the list of cities for the hovered department (no header) */}
            {departmentToCities[hoveredCity] ? (
              <div className="tooltip-cities">
                {getDepartmentDisplayCities(hoveredCity).join(', ')}
              </div>
            ) : (
              <div className="tooltip-cities">{hoveredCity}</div>
            )}
          </div>
        )}
      </div>

      {showCreateReviewModal && (
        <div className="modal-overlay" onClick={closeCreateReviewModal}>
          <div className="modal-content modal-zoom-in temoignages-review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Laisser une note</h3>
              <button className="modal-close" onClick={closeCreateReviewModal}>x</button>
            </div>
            <div className="modal-body">
              <p className="temoignages-review-modal-region">Ville: {selectedCities.join(', ')}</p>

              {reviewFormFields.length > 0 ? reviewFormFields.sort((a, b) => (a.order || 0) - (b.order || 0)).map((field) => {
                if (field.type === 'rating') {
                  return (
                    <div key={field.id} className="form-group">
                      <label>{field.label}</label>
                      <div className="temoignages-rating-picker" onMouseLeave={() => setHoverRating(0)}>
                        {[1, 2, 3, 4, 5].map((star) => {
                          const active = star <= (hoverRating || reviewDraft.rating);
                          return (
                            <button
                              type="button"
                              key={star}
                              className={`temoignages-star-btn ${active ? 'active' : ''}`}
                              onMouseEnter={() => setHoverRating(star)}
                              onClick={() => setReviewDraft((prev) => ({ ...prev, rating: star }))}
                              aria-label={`Noter ${star} sur 5`}
                            >
                              ★
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                if (field.type === 'textarea') {
                  return (
                    <div key={field.id} className="form-group">
                      <label>{field.label}{field.required ? ' *' : ''}</label>
                      <textarea
                        rows={5}
                        value={reviewDraft[field.id] || ''}
                        onChange={(e) => setReviewDraft((prev) => ({ ...prev, [field.id]: e.target.value }))}
                        placeholder={field.placeholder || ''}
                      />
                    </div>
                  );
                }

                if (field.type === 'checkbox') {
                  const hasOptions = Array.isArray(field.options) && field.options.length > 0;
                  if (hasOptions) {
                    const selectedValues = Array.isArray(reviewDraft[field.id]) ? reviewDraft[field.id] : [];
                    return (
                      <div key={field.id} className="form-group">
                        <label>{field.label}{field.required ? ' *' : ''}</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                          {field.options.map((opt) => (
                            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'normal', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={selectedValues.includes(opt)}
                                onChange={(e) => {
                                  setReviewDraft((prev) => {
                                    const current = Array.isArray(prev[field.id]) ? [...prev[field.id]] : [];
                                    if (e.target.checked) {
                                      current.push(opt);
                                    } else {
                                      const idx = current.indexOf(opt);
                                      if (idx > -1) current.splice(idx, 1);
                                    }
                                    return { ...prev, [field.id]: current };
                                  });
                                }}
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={field.id} className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={Boolean(reviewDraft[field.id])}
                          onChange={(e) => setReviewDraft((prev) => ({ ...prev, [field.id]: e.target.checked }))}
                          style={{ marginRight: 8 }}
                        />
                        {field.label}{field.required ? ' *' : ''}
                      </label>
                    </div>
                  );
                }

                if (field.type === 'dropdown' || field.type === 'select') {
                  return (
                    <div key={field.id} className="form-group">
                      <label>{field.label}{field.required ? ' *' : ''}</label>
                      <select
                        value={reviewDraft[field.id] || ''}
                        onChange={(e) => setReviewDraft((prev) => ({ ...prev, [field.id]: e.target.value }))}
                      >
                        <option value="">{field.placeholder || 'Selectionnez...'}</option>
                        {(field.options || []).map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  );
                }

                return (
                  <div key={field.id} className="form-group">
                    <label>{field.label}{field.required ? ' *' : ''}</label>
                    <input
                      type="text"
                      value={reviewDraft[field.id] || ''}
                      onChange={(e) => setReviewDraft((prev) => ({ ...prev, [field.id]: e.target.value }))}
                      placeholder={field.placeholder || ''}
                    />
                  </div>
                );
              }) : (
                <>
                  <div className="form-group">
                    <label>Votre nom</label>
                    <input
                      type="text"
                      value={reviewDraft.reviewer_name}
                      onChange={(e) => setReviewDraft((prev) => ({ ...prev, reviewer_name: e.target.value }))}
                      placeholder="Ex: Ahmed"
                    />
                  </div>
                  <div className="form-group">
                    <label>Votre note</label>
                    <div className="temoignages-rating-picker" onMouseLeave={() => setHoverRating(0)}>
                      {[1, 2, 3, 4, 5].map((star) => {
                        const active = star <= (hoverRating || reviewDraft.rating);
                        return (
                          <button
                            type="button"
                            key={star}
                            className={`temoignages-star-btn ${active ? 'active' : ''}`}
                            onMouseEnter={() => setHoverRating(star)}
                            onClick={() => setReviewDraft((prev) => ({ ...prev, rating: star }))}
                            aria-label={`Noter ${star} sur 5`}
                          >
                            ★
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Votre avis</label>
                    <textarea
                      rows={5}
                      value={reviewDraft.review_text}
                      onChange={(e) => setReviewDraft((prev) => ({ ...prev, review_text: e.target.value }))}
                      placeholder="Partagez votre experience de residence..."
                    />
                  </div>
                </>
              )}

              {reviewSubmitError ? <div className="form-error">{reviewSubmitError}</div> : null}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={closeCreateReviewModal} disabled={reviewSubmitting}>Annuler</button>
              <button className="btn btn-primary" onClick={submitReview} disabled={reviewSubmitting}>
                {reviewSubmitting ? 'Publication...' : 'Publier'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Temoignages;
